/**
 * Passkey (WebAuthn) management hooks — Security Centre.
 *
 * Wraps the self-hosted WebAuthn endpoints on the tenant-onboarding Function App
 * (see lib/webauthn.ts for the ceremony helpers + the Supabase-can't-do-native-
 * WebAuthn rationale). All calls run on the OPERATOR's own session.
 *
 * Query key `['webauthn', 'credentials']` is invalidated after enroll/remove so
 * the list and the step-up "use passkey" affordance stay in sync.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  enrollPasskey,
  listPasskeys,
  removePasskey,
  type PasskeySummary,
} from '../webauthn';

export const WEBAUTHN_CREDENTIALS_KEY = ['webauthn', 'credentials'] as const;

/** List the caller's enrolled passkeys. */
export function usePasskeys() {
  return useQuery<PasskeySummary[]>({
    queryKey: WEBAUTHN_CREDENTIALS_KEY,
    queryFn: () => listPasskeys(),
    // Feature-off (503 WEBAUTHN_NOT_CONFIGURED) is a stable state, not a
    // transient — retrying just spams the console (2026-07-18 sweep).
    retry: (failureCount, error) =>
      (error as { code?: string } | null)?.code !== 'WEBAUTHN_NOT_CONFIGURED' && failureCount < 2,
  });
}

/** Enroll a new passkey (runs the browser registration ceremony). */
export function useEnrollPasskey() {
  const qc = useQueryClient();
  return useMutation<void, Error, { deviceName: string }>({
    mutationFn: ({ deviceName }) => enrollPasskey(deviceName),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WEBAUTHN_CREDENTIALS_KEY });
    },
  });
}

/** Remove one of the caller's passkeys by its document id. */
export function useRemovePasskey() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => removePasskey(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WEBAUTHN_CREDENTIALS_KEY });
    },
  });
}

export type { PasskeySummary } from '../webauthn';
